import { MenuItem } from 'primeng/api';
import { normalizeSearchText, scoreTextMatch } from './text-search.utils';

export function getChildItems(item: MenuItem): MenuItem[] {
    return (item.items ?? []).filter(child => child.visible !== false) as MenuItem[];
}

export function normalizeRouterLink(routerLink: MenuItem['routerLink']): string | null {
    if (!routerLink) {
        return null;
    }

    if (Array.isArray(routerLink)) {
        const segments = routerLink
            .map(segment => String(segment).replace(/^\/+/, ''))
            .filter(Boolean);
        return `/${segments.join('/')}`;
    }

    return routerLink.startsWith('/') ? routerLink : `/${routerLink}`;
}

export function normalizeUrl(url: string): string {
    return url.split('?')[0].replace(/\/+$/, '') || '/';
}

export function itemKey(item: MenuItem, keyParts: string[] = []): string {
    const label = item.label ?? 'item';
    const routerLink = normalizeRouterLink(item.routerLink) ?? '';
    return [...keyParts, label, routerLink].filter(Boolean).join('>');
}

export function menuIcon(item: MenuItem): string {
    return item.icon || 'pi pi-circle';
}

export function isMenuItemExactActive(item: MenuItem, currentUrl: string): boolean {
    const routerLink = normalizeRouterLink(item.routerLink);
    return !!routerLink && normalizeUrl(currentUrl) === normalizeUrl(routerLink);
}

export function isMenuItemActive(item: MenuItem, currentUrl: string): boolean {
    return isMenuItemExactActive(item, currentUrl)
        || getChildItems(item).some(child => isMenuItemActive(child, currentUrl));
}

export function trackMenuItem(item: MenuItem, index: number): string {
    return item.id ?? itemKey(item, [String(index)]);
}

export function buildSidebarMenu(
    items: MenuItem[],
    searchTerm: string,
    currentUrl: string,
    expandedMenuKeys: Record<string, boolean>
): MenuItem[] {
    const normalizedTerm = normalizeSearchText(searchTerm);

    return items
        .map((item, index) => buildSidebarMenuItem(item, normalizedTerm, currentUrl, expandedMenuKeys, [String(index)]))
        .filter((item): item is MenuItem => item !== null);
}

export function findActiveMenuItem(items: MenuItem[], currentUrl: string): MenuItem | null {
    return flattenMenuItems(items)
        .filter(item => item.visible !== false)
        .map(item => ({
            item,
            score: routeMatchScore(item, currentUrl)
        }))
        .filter(match => match.score > 0)
        .sort((a, b) => b.score - a.score)[0]?.item ?? null;
}

function buildSidebarMenuItem(
    item: MenuItem,
    normalizedTerm: string,
    currentUrl: string,
    expandedMenuKeys: Record<string, boolean>,
    keyParts: string[]
): MenuItem | null {
    if (item.visible === false) {
        return null;
    }

    const key = itemKey(item, keyParts);
    const configuredChildren = item.items ?? [];
    const matchesSearch = menuItemMatchesSearch(item, normalizedTerm);
    const childSearchTerm = matchesSearch ? '' : normalizedTerm;
    const children = configuredChildren
        .map((child, index) => buildSidebarMenuItem(child, childSearchTerm, currentUrl, expandedMenuKeys, [...keyParts, String(index)]))
        .filter((child): child is MenuItem => child !== null);

    const isAncestorOfActive = children.some(child => isMenuItemActive(child, currentUrl));
    const persistedExpanded = expandedMenuKeys[key];

    if (!matchesSearch && !children.length) {
        return null;
    }

    if (configuredChildren.length && !children.length && !normalizeRouterLink(item.routerLink)) {
        return null;
    }

    const cloned: MenuItem = {
        ...item,
        id: item.id ?? key
    };

    if (children.length) {
        cloned.items = children;
        cloned.expanded = normalizedTerm ? true : persistedExpanded ?? isAncestorOfActive;
    }

    return cloned;
}

function flattenMenuItems(items: MenuItem[]): MenuItem[] {
    return items
        .filter(item => item.visible !== false)
        .flatMap(item => [item, ...flattenMenuItems(getChildItems(item))]);
}

function routeMatchScore(item: MenuItem, currentUrl: string): number {
    const routerLink = normalizeRouterLink(item.routerLink);
    if (!routerLink) {
        return 0;
    }

    const normalizedCurrentUrl = normalizeUrl(currentUrl);
    const targetUrl = normalizeUrl(routerLink);

    if (normalizedCurrentUrl === targetUrl) {
        return 1000 + targetUrl.length;
    }

    return normalizedCurrentUrl.startsWith(`${targetUrl}/`) ? targetUrl.length : 0;
}

function menuItemMatchesSearch(item: MenuItem, normalizedTerm: string): boolean {
    if (!normalizedTerm) {
        return true;
    }

    const searchableText = [
        item.label,
        normalizeRouterLink(item.routerLink)
    ].filter(Boolean).join(' ');

    return scoreTextMatch(searchableText, normalizedTerm, 100) > 0;
}
